import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

/**
 * Canonical admin page header — the ONE band at the top of every admin route.
 *
 * Replaces three competing patterns that used to coexist on a single screen:
 *
 *  1. the shell's `.adm-pagehead` title band (data-driven from a URL→title map,
 *     with no slot for an action),
 *  2. a second `.adm-page__header` rendered by the page itself — so detail and
 *     create routes stacked two header bands, and
 *  3. a `<div class="d-flex justify-content-end px-3 pt-3">` CTA row rendered by
 *     the *parent* route component above `<router-outlet>`. Because the parent
 *     stays mounted across child navigations, "Create operator" kept showing
 *     while you were already on the create-operator page — the button that
 *     appeared to move as you navigated.
 *
 * Declaring the action here fixes (3) structurally: the header belongs to the
 * LEAF component, so it unmounts with the page and cannot leak onto a child
 * route. There is nothing left to remember to hide.
 *
 * Pair it with `data: { ownHeader: true }` on the route so the shell suppresses
 * its own band (see `AdminShellComponent.hideHeaderBand`). Without that flag you
 * get two bands, and FF-008 (`npm run arch:fitness`,
 * `docs/architecture/fitness-functions/ff-008-admin-header-contract.ts`) fails
 * the build on the mismatch — it is static, so it runs in CI. Its browser-based counterpart
 * `tools/scripts/check-admin-layout.mjs` measures real geometry but needs
 * playwright plus a served, authenticated build, so it is a local check.
 *
 * Visual chrome lives in the shared design-tokens partial
 * `libs/front/shared/design-tokens/src/_admin-tables.scss` (`.adm-page__*`) —
 * never per-component SCSS, so width and rhythm stay in one place.
 *
 * ```html
 * <bodyanalytics-adm-page-header eyebrow="Truck staff" title="Operators" [showBack]="false">
 *   <a page-actions class="btn btn-primary" [routerLink]="createOperatorLink">
 *     <i class="bx bx-user-plus" aria-hidden="true"></i> Create operator
 *   </a>
 * </bodyanalytics-adm-page-header>
 * ```
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-adm-page-header',
  standalone: true,
  templateUrl: './adm-page-header.component.html',
})
export class AdmPageHeaderComponent {
  /** Page name — rendered as the `<h1>` at 22px Fraunces (R-LAYOUT-01). */
  readonly title = input.required<string>();

  /**
   * Small mono uppercase kicker above the title showing parent context
   * ("Truck staff", "All regions", "Calendar · North Texas") — R-LAYOUT-02.
   */
  readonly eyebrow = input<string | undefined>(undefined);

  /** Back-link label. Ignored when `showBack` is false. */
  readonly backLabel = input<string>('Go back');

  /**
   * Whether to render the back link. List/dashboard routes are the top of their
   * own stack and set this false; create and detail routes leave it on.
   */
  readonly showBack = input<boolean>(true);

  /** User pressed the back link. */
  readonly back = output<void>();
}
